import { RefObject, useLayoutEffect, useState } from 'react';

/** An element's content box, CSS px. */
export interface ElementSize {
    readonly widthPx: number;
    readonly heightPx: number;
}

/**
 * The live size of the element a ref holds: read once when it mounts, then
 * followed through a `ResizeObserver` released when the element unmounts.
 * `undefined` until the first read. The value keeps its identity while the
 * size is unchanged, so an effect can key on it. Without `ResizeObserver`
 * the first read stands.
 */
export function useElementSize(
    ref: RefObject<HTMLElement | null>
): ElementSize | undefined {
    const [size, setSize] = useState<ElementSize | undefined>(undefined);

    useLayoutEffect(() => {
        const element = ref.current;
        if (!element) {
            return undefined;
        }
        const measure = (widthPx: number, heightPx: number) => {
            setSize((current) =>
                current?.widthPx === widthPx && current.heightPx === heightPx
                    ? current
                    : { widthPx, heightPx }
            );
        };
        const rect = element.getBoundingClientRect();
        measure(rect.width, rect.height);
        if (typeof ResizeObserver === 'undefined') {
            return undefined;
        }
        const observer = new ResizeObserver((entries) => {
            const content = entries[0]?.contentRect;
            if (content) {
                measure(content.width, content.height);
            }
        });
        observer.observe(element);
        return () => observer.disconnect();
    }, [ref]);

    return size;
}
